import type { InvalidateQueryFilters, QueryClient } from '@tanstack/react-query';
import { BulkRetrieveFeatureStatusesRequest } from '@squareup/shared-types-protos/squareup/featurama/service';
import { ALL_FEATURES } from '../constants';
import { getFallbackQueryClient } from './queryClient';
import { refreshQuery } from './refreshQuery';

export async function invalidateFeaturamaCache() {
  // Use dynamic import to delay module loading until after DashboardQueryContext is initialized
  const { featuramaClient } = await import('@squareup/dashboard-query/api/featurama/rpc');

  const request = BulkRetrieveFeatureStatusesRequest.create({
    consistent: true,
    keys: ALL_FEATURES,
  });
  await featuramaClient.bulkRetrieveFeatureStatuses(request);
}

export async function refreshFeatureStatuses(queryClient?: QueryClient) {
  const dashboardQueryQueryKey = ['featurama', 'externalBulkRetrieveFeatureStatuses'];
  // Setting `consistent` to true ensures the server cache is properly updated,
  // providing a consistent read of the data. We call this here so that subsequent
  // calls to `BulkRetrieveFeatureStatuses` from our active queries retrieve the
  // latest data from a subscription update.
  await invalidateFeaturamaCache();

  await refreshQuery(dashboardQueryQueryKey, queryClient);
}

export async function refreshFeatureSetStatuses(queryClient?: QueryClient) {
  const dashboardQueryQueryKey = ['featurama', 'externalBulkRetrieveFeatureSetStatuses'];
  await refreshQuery(dashboardQueryQueryKey, queryClient);
}

/**
 * Invalidates subscription-related queries to refresh cached data.
 *
 * @param {QueryClient} [queryClient] - Optional `QueryClient` instance for invalidation; if not provided, the fallback instance is used.
 * @param {InvalidateQueryFilters} [options] - Optional filters for query invalidation.
 * @returns {Promise<void>} Resolves when all queries are invalidated.
 */
export async function invalidateSubscriptionStates(queryClient?: QueryClient, options?: InvalidateQueryFilters) {
  // Use dynamic import to avoid static imports of lazy-loaded dashboard-query library
  const { subscriptions } = await import('@squareup/dashboard-query/queries/subscriptions');

  const client = queryClient ?? getFallbackQueryClient();

  // this call ensures that the server updates its cache and subsequent calls
  // to `BulkRetrieveFeatureStatuses` from active queries retrieve the latest data
  await invalidateFeaturamaCache();

  await Promise.all(subscriptions.keys.invalidationKeys().map((queryKey) => client.invalidateQueries({ queryKey, ...(options ?? {}) })));
}
